<template>
	<article :class="['module', props.applyHover ? 'offering-card' : '']" :data-test="offering.name">
		<div class="display-flex-md mt-50-smo module-section">
			<div class="col-8-md py-50-md pl-50-md pr-300-lg pr-150-md">
				<component :is="fundInfoComponent.component" v-bind="fundInfoComponent.props">
					<div
						v-if="isAccreditedOnly"
						class="overline font-weight-bold mb-50 text-color-content-tertiary eta"
						data-test="accredited-only"
						>Accredited Only</div
					>
					<div
						class="display-flex align-items-center justify-content-space-between-smo text-color-content-primary"
					>
						<h3 class="heading-5">{{ offering.name }}</h3>
						<BaseSvgIcon
							v-if="offeringLink"
							name="chevron"
							width="24"
							height="24"
							dir="right"
							class="ml-100"
						/>
					</div>
					<div class="display-flex align-items-center mt-100 mb-150">
						<StatusTag
							v-if="offeringOpenStatus && !appStore.isAdvisor"
							:text="offeringOpenStatus"
							:background="offeringStatusClass"
						/>
						<Tag :text="offering.assetClass.label" />
					</div>
					<p class="dark zeta-smo" v-html="offering.fundSummary" />
					<p
						v-if="isReservationEligible && isNonAdvisorUser"
						class="p-100 dark bg-color-greige"
						v-html="windowReservationDisplayText"
					/>
				</component>
				<slot name="additional-data" />
			</div>
			<div class="col-4-md py-50-md pl-300-lg pl-150-md pr-50-md bl-md pt-150-smo">
				<div
					v-for="(detail, i) in offeringCardDetails"
					:key="i"
					class="pb-100 display-flex align-items-center justify-content-space-between zeta-smo"
					:class="{ 'pt-100 bt': i !== 0 }"
				>
					<span class="offering-card-detail-key">{{ detail.key }}</span>
					<span class="offering-card-detail-value">{{ detail.value }}</span>
				</div>
				<div v-if="isNonAdvisorUser && directInvestmentEligibility && vintageId" class="mt-50">
					<DirectInvestCTA
						:direct-investment-eligibility="directInvestmentEligibility"
						:offering-vintage-id="vintageId"
						:display-unavailable-state="true"
						classes="button-block"
						:trigger="props.trigger"
					/>
				</div>
			</div>
		</div>
	</article>
</template>
<script setup lang="ts">
import { currency, date, deorphanText } from '@filters/shared-filters';
import { DirectInvestmentEligibility, Fund, FundDirectInvestDetails } from 'types/funds';
import { KeyValue, Link, TagBackground } from 'types/layout';
import BaseLink from '@components/_base-link.vue';
import { buildOfferingDetailsLink } from '@utils/offerings';
import { computed } from 'vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import DirectInvestCTA from '@components/offerings/direct-invest-cta.vue';
import { DynamicComponent } from 'types/vue';
import { InvestmentTriggerQuery } from 'types/checkout';
import StatusTag from '@components/tags/status-tag.vue';
import Tag from '@components/tags/tag-component.vue';
import { useAppStore } from '@stores/app';
import { windowReservationDescription } from '@constants/window-reservation';
import type { Component } from 'vue';

interface Props {
	applyHover?: boolean;
	maxInvestment?: string;
	minInvestment?: string;
	offering: Fund;
	trigger?: InvestmentTriggerQuery;
}

const props = withDefaults(defineProps<Props>(), {
	applyHover: true,
	maxInvestment: undefined,
	minInvestment: undefined,
	trigger: undefined
});

const appStore = useAppStore();

// computed
const offeringLink = computed((): Link | null => {
	return props.offering.fundriseId ? buildOfferingDetailsLink(props.offering.fundriseId) : null;
});

const directInvestmentDetails = computed((): FundDirectInvestDetails | null => {
	return props.offering.directInvestmentDetails ?? null;
});

const directInvestmentEligibility = computed((): DirectInvestmentEligibility | null => {
	return directInvestmentDetails.value?.directInvestmentEligibility ?? null;
});

const isAccreditedOnly = computed((): boolean => directInvestmentDetails.value?.accreditedOnly ?? false);

const isNonAdvisorUser = computed((): boolean => {
	return appStore.isAuthenticated && !appStore.isAdvisor;
});

const isReservationEligible = computed((): boolean => directInvestmentEligibility.value === 'RESERVATION_ELIGIBLE');

const vintageId = computed((): string => {
	return directInvestmentDetails.value?.reitOfferingVintageId ?? '';
});

const offeringOpenStatus = computed((): string => {
	return props.offering.offeringAvailabilityStatus?.label || '';
});

const offeringStatusClass = computed((): TagBackground => {
	switch (props.offering.offeringAvailabilityStatus?.name) {
		case 'OPEN':
			return 'green';
		case 'COMING_SOON':
		case 'RESERVATION_AVAILABLE':
			return 'blue';
		case 'CLOSED':
		default:
			return 'default';
	}
});

const offeringCardDetails = computed((): Array<KeyValue> => {
	return [
		{
			key: 'Objective',
			value: props.offering.objective?.name ?? ''
		},
		{
			key: 'Net asset value',
			value: props.offering?.fundAum ?? ''
		},
		{
			key: 'Inception date',
			value: props.offering?.inceptionDate
				? date(props.offering.inceptionDate, CustomSpacetimeFormat.MONTH_SHORT_YEAR)
				: ''
		},
		{
			key: 'Next available window',
			value: props.offering?.reservationWindow?.reservationWindow.name ?? ''
		},
		{
			key: 'Minimum investment',
			value: props.minInvestment ? currency(props.minInvestment, 0) : ''
		},
		{
			key: 'Maximum investment',
			value: props.maxInvestment ? currency(props.maxInvestment, 0) : ''
		}
	].filter((item) => !!item.value);
});

const windowReservationDisplayText = computed((): string =>
	deorphanText(windowReservationDescription(props.offering.reservationWindow?.reservationWindow.name ?? ''))
);

const fundInfoComponent = computed((): DynamicComponent => {
	if (offeringLink.value) {
		return {
			component: BaseLink,
			props: {
				link: offeringLink.value
			}
		};
	} else {
		return {
			component: 'div' as unknown as Component
		};
	}
});
</script>
<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;

.offering-card {
	transition: 0.25s all ease;

	&:hover {
		transform: translateY(-2px);
	}
}

.offering-card-detail-key {
	color: token('content-secondary');
}
</style>
